import React, { useState, useEffect } from 'react';
import { workshopData } from '../data/mock';
import { useGlobalTimer } from '../hooks/useGlobalTimer';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Cog,
  Target,
  Sparkles,
  TrendingUp,
  Zap,
  Check,
  ArrowRight,
  X,
  Flame,
  Shield,
  Star,
  Youtube,
  Instagram
} from 'lucide-react';

const iconMap = { Target, Cog, Sparkles, TrendingUp, Zap };

// YouTube Video Embed Component - Simple, no autoplay
const YouTubeEmbed = ({ videoId }) => {
  return (
    <div className="youtube-video-container" data-animate>
      <iframe
        className="youtube-video"
        src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
        title="Instructor Video"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        loading="lazy"
      />
    </div>
  );
};

// Global Timer Display Component - New Box Style
// Uses single shared state - all timers are synced
const TimerDisplay = ({ compact = false }) => {
  const { minutes, seconds } = useGlobalTimer();
  
  return (
    <div className={`timer-box-display ${compact ? 'timer-compact' : ''}`}>
      <div className="timer-unit">
        <span className="timer-value">{String(minutes).padStart(2, '0')}</span>
        <span className="timer-label">Minutes</span>
      </div>
      <div className="timer-unit">
        <span className="timer-value">{String(seconds).padStart(2, '0')}</span>
        <span className="timer-label">Seconds</span>
      </div>
    </div>
  );
};

// Sticky Bottom Bar - Left: Price + Timer, Right: CTA
const StickyBottomBar = ({ show, onCTAClick }) => {
  return (
    <div className={`sticky-bar-premium ${show ? 'show' : ''}`}>
      <div className="sticky-bar-split">
        <div className="sticky-left">
          <div className="sticky-price-line">
            <span className="sticky-old">₹{workshopData.pricing.originalPrice}</span>
            <span className="sticky-new">₹{workshopData.pricing.price}</span>
          </div>
          <div className="sticky-timer-line">
            <TimerDisplay compact={true} />
          </div>
        </div>
        <Button className="sticky-cta-btn gold-cta-animated" onClick={onCTAClick}>
          Grab Your Seat
          <ArrowRight size={14} />
        </Button>
      </div>
    </div>
  );
};

const LandingPage = () => {
  const [showStickyBar, setShowStickyBar] = useState(false);

  // Sticky bar: appears after scrolling past hero section (25% of viewport height)
  useEffect(() => {
    const handleScroll = () => {
      const scrollThreshold = window.innerHeight * 0.25; // 25% of viewport
      if (window.scrollY > scrollThreshold) {
        setShowStickyBar(true);
        // Remove listener once sticky bar is shown - it stays visible
        window.removeEventListener('scroll', handleScroll);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Check initial scroll position (in case page loads scrolled)
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Premium scroll-reveal animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animated');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleCTAClick = () => {
    window.location.href = workshopData.payment.cashfreeLink;
  };

  const getYouTubeEmbedUrl = (url) => {
    const videoId = url.split('youtu.be/')[1]?.split('?')[0] || url.split('v=')[1]?.split('&')[0];
    return `https://www.youtube.com/embed/${videoId}`;
  };

  return (
    <div className="landing-premium">
      <div className="premium-glow-bg"></div>

      {/* ============ HERO — Rebuilt ============ */}
      <section className="hero-premium" data-animate>
        <div className="hero-glow-orb"></div>
        <div className="container-premium hero-container">

          {/* Live Badge */}
          <div className="hero-live-badge" data-animate>
            <span className="live-dot"></span>
            <span className="live-label">LIVE WORKSHOP</span>
            <span className="live-divider">|</span>
            <span className="live-sub">2-Day AI Implementation</span>
          </div>

          {/* Headline */}
          <h1 className="hero-headline" data-animate>
            {workshopData.hero.headline}
          </h1>

          {/* Subhead */}
          <p className="hero-subhead-new" data-animate>
            Deploy practical AI across marketing, sales and ops<br />
            so your business runs faster, leaner and more profitable.
          </p>

          {/* Video Embed */}
          <div className="hero-video-wrap" data-animate>
            <div className="video-frame-premium">
              <iframe
                className="youtube-embed"
                src={getYouTubeEmbedUrl(workshopData.hero.videoUrl)}
                title="Workshop Preview"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>

          {/* 4 Info Cards */}
          <div className="hero-info-grid" data-animate>
            <div className="hero-info-card">
              <span className="info-label">Day 1</span>
              <span className="info-value">27th March</span>
            </div>
            <div className="hero-info-card">
              <span className="info-label">Day 2</span>
              <span className="info-value">28th March</span>
            </div>
            <div className="hero-info-card">
              <span className="info-label">Duration</span>
              <span className="info-value">2 Hours Each Day</span>
            </div>
            <div className="hero-info-card">
              <span className="info-label">Platform</span>
              <span className="info-value">Zoom</span>
            </div>
          </div>

          {/* Urgency */}
          <p className="hero-urgency" data-animate>
            Only limited founder seats available.
          </p>

          {/* CTA Group */}
          <div className="hero-cta-group" data-animate>
            <button className="hero-cta-gold gold-cta-animated" onClick={handleCTAClick} data-testid="hero-cta-button">
              Register Now
              <ArrowRight size={18} />
            </button>
            <button className="hero-cta-secondary" onClick={handleCTAClick}>
              Limited Seats
            </button>
          </div>

        </div>
      </section>

      {/* ============ GRAB YOUR SEAT SECTION ============ */}
      <section className="section-premium section-dark grab-seat-section" id="grab-seat" data-animate>
        <div className="container-premium">
          <div className="grab-seat-card">
            <div className="grab-seat-glow"></div>

            <h2 className="grab-seat-heading" data-animate>Grab Your Seat</h2>
            <p className="grab-seat-subtext" data-animate>Secure your spot before price increases.</p>

            <div className="grab-seat-price-row" data-animate>
              <span className="grab-seat-original">₹{workshopData.pricing.originalPrice}</span>
              <span className="grab-seat-arrow">→</span>
              <span className="grab-seat-current">₹{workshopData.pricing.price}</span>
            </div>

            <div className="grab-seat-timer" data-animate>
              <TimerDisplay />
            </div>

            <div className="grab-seat-cta-group" data-animate>
              <button className="grab-seat-cta-btn gold-cta-animated" onClick={handleCTAClick} data-testid="grab-seat-cta">
                Grab Your Seat
                <ArrowRight size={18} />
              </button>
              <p className="grab-seat-cta-sub">Limited Seats Available</p>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Get Section */}
      <section className="section-premium section-light" data-animate>
        <div className="container-premium">
          <h2 className="section-heading">{workshopData.whatYouLearn.heading}</h2>
          <p className="section-intro">{workshopData.whatYouLearn.intro}</p>

          <div className="cards-grid">
            {workshopData.whatYouLearn.items.map((item, index) => {
              const Icon = iconMap[item.icon];
              return (
                <div key={index} className="feature-card" data-animate>
                  <div className="feature-icon">
                    <Icon size={28} />
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              );
            })}
          </div>

          <p className="section-footer">{workshopData.whatYouLearn.footer}</p>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="section-premium section-dark" data-animate>
        <div className="container-premium">
          <h2 className="section-heading">{workshopData.whoThisIsFor.heading}</h2>

          <div className="personas-grid">
            {workshopData.whoThisIsFor.personas.map((persona, index) => (
              <div key={index} className="persona-card" data-animate>
                <h3>{persona.title}</h3>
                <p>{persona.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Is NOT For */}
      <section className="section-premium section-light" data-animate>
        <div className="container-premium">
          <h2 className="section-heading not-for-heading">{workshopData.whoThisIsNotFor.heading}</h2>

          <div className="not-for-grid">
            {workshopData.whoThisIsNotFor.items.map((item, index) => (
              <div key={index} className="not-for-card" data-animate>
                <X size={24} className="not-for-icon" />
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-premium section-dark" data-animate>
        <div className="container-premium">
          <h2 className="section-heading">{workshopData.testimonials.heading}</h2>

          <div className="testimonials-grid">
            {workshopData.testimonials.reviews.map((review, index) => (
              <div key={index} className="testimonial-card" data-animate>
                <div className="stars">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} size={16} fill="#D4AF37" stroke="#D4AF37" />
                  ))}
                </div>
                <p className="testimonial-text">"{review.review}"</p>
                <div className="reviewer">
                  <span className="reviewer-name">{review.name}</span>
                  <span className="reviewer-title">{review.title}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Instructor Section - Cinematic Authority Version */}
      <section className="section-premium section-light instructor-cinematic" data-animate>
        <div className="container-premium">
          
          {/* Section Heading */}
          <p className="instructor-section-heading">Meet Your Instructor</p>

          {/* Profile Content */}
          <div className="instructor-profile" data-animate>
            <div className="profile-header">
              <img 
                src={workshopData.instructor.imageUrl} 
                alt={workshopData.instructor.name}
                className="profile-image"
              />
              <div className="profile-text">
                <h2 className="profile-name">{workshopData.instructor.name}</h2>
                <p className="profile-role">{workshopData.instructor.title}</p>
              </div>
            </div>

            <div className="profile-description">
              {workshopData.instructor.description.map((para, index) => (
                <p key={index}>{para}</p>
              ))}
            </div>

            <p className="profile-highlight">{workshopData.instructor.highlight}</p>

            {/* Social Buttons */}
            <div className="social-buttons">
              <a 
                href={workshopData.instructor.social.youtube} 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-btn youtube-btn"
              >
                <Youtube size={18} />
                YouTube
              </a>
              <a 
                href={workshopData.instructor.social.instagram} 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-btn instagram-btn"
              >
                <Instagram size={18} />
                Instagram
              </a>
            </div>

            {/* Stats */}
            <div className="instructor-stats-row">
              {workshopData.instructor.stats.map((stat, index) => (
                <div key={index} className="stat-box-cinematic">
                  <div className="stat-number-gold">{stat.number}</div>
                  <div className="stat-label-small">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* YouTube Video Embed - Between Stats and Featured In */}
          <YouTubeEmbed videoId="Cy2MEgqyN9I" />

          {/* Featured In - Clickable Logos */}
          <div className="featured-in-cinematic" data-animate>
            <p className="featured-heading-cinematic">{workshopData.mediaStrip.heading}</p>
            <div className="logos-container-cinematic">
              {workshopData.mediaStrip.logos.map((logo, index) => (
                <a 
                  key={index} 
                  href={logo.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="logo-link"
                >
                  <img src={logo.url} alt={logo.name} className="media-logo-cinematic" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Bonuses */}
      <section className="section-premium section-dark" data-animate>
        <div className="container-premium">
          <h2 className="section-heading bonuses-heading">
            {workshopData.bonuses.heading}
          </h2>

          <div className="bonuses-list">
            {workshopData.bonuses.items.map((bonus, index) => (
              <div key={index} className="bonus-item" data-animate>
                <Check size={20} />
                <span>{bonus}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Money Back Guarantee */}
      <section className="section-premium section-light" data-animate>
        <div className="container-premium">
          <div className="guarantee-card">
            <Shield size={48} className="guarantee-icon" />
            <h2>{workshopData.moneyBackGuarantee.heading}</h2>
            <p>{workshopData.moneyBackGuarantee.description}</p>
          </div>
        </div>
      </section>

      {/* Investment Section */}
      <section className="section-premium section-dark" data-animate>
        <div className="container-premium">
          <div className="pricing-premium">
            <div className="pricing-glow"></div>
            
            <h2 className="pricing-heading">Investment</h2>
            
            <div className="price-display">
              <span className="price-original">₹{workshopData.pricing.originalPrice}</span>
              <span className="price-current">₹{workshopData.pricing.price}</span>
            </div>

            <div className="pricing-urgency">
              <TimerDisplay />
              <span className="seats-urgency">
                <Flame size={16} />
                {workshopData.stickyBar.seatsLeft} seats left
              </span>
            </div>

            <div className="value-stack">
              {workshopData.pricing.valueStack.map((item, index) => (
                <div key={index} className="value-item">
                  <Check size={18} />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            <Button className="pricing-cta gold-cta-animated" onClick={handleCTAClick} data-testid="pricing-cta-button">
              Grab Your Seat
              <ArrowRight size={20} />
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-premium section-light" data-animate>
        <div className="container-premium">
          <h2 className="section-heading">Frequently Asked Questions</h2>

          <Accordion type="single" collapsible className="faq-accordion">
            {workshopData.faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="faq-item">
                <AccordionTrigger className="faq-question">{faq.question}</AccordionTrigger>
                <AccordionContent className="faq-answer">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-premium section-dark final-cta-section" data-animate>
        <div className="final-cta-glow"></div>
        <div className="container-premium">
          <div className="final-cta">
            <h2>{workshopData.finalCta.headline}</h2>
            <p>{workshopData.finalCta.subheadline}</p>
            
            <Button className="final-cta-btn gold-cta-animated" onClick={handleCTAClick} data-testid="final-cta-button">
              Join Now
              <ArrowRight size={24} />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer-premium">
        <p>© 2026 Kave AI. All rights reserved.</p>
      </footer>

      {/* Sticky Bottom Bar */}
      <StickyBottomBar show={showStickyBar} onCTAClick={handleCTAClick} />
    </div>
  );
};

export default LandingPage;
